import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { defaultDifficulty, PictureSet, pictureSetMap } from "../Setting_difficulty";
import { HTML5Backend } from "react-dnd-html5-backend";
import CanvasStripper from "../canvas/CanvasStripper";

import "../styles/StripesBoard.css";
import { loadSvgImages } from '../utils/loadImages';

// Constants

const INITIAL_MESSAGE = "Восстанови картинку";
const WIN_MESSAGE = "Поздравляю! Задание выполнено";
const START_MESSAGE = "Начни игру";

// Types
interface StripItem {
  urls: string;
  index: number;
}

interface StripProps {
  urls: string;
  index: number;
  offset: string[];
  shouldAnimate: boolean;
  direction: string;
}

interface BorderCellProps {
  index: number;
  strip: StripItem | null;
  onDrop: (item: StripItem, borderIndex: number) => void;
  onStripClick: (borderIndex: number) => void;
}

interface BoardProps {
  setisOpenPanel: (value: boolean) => void;
  setisRunningTime: (value: boolean) => void;
  isStartGame: boolean;
  gameCompleted: boolean;
  setGameCompleted: (value: boolean) => void;
  pictureSet?: PictureSet;
  difficulty?: number;
  onResetGame?: () => void; 
}

// Helper functions
const generateOffsets = (count: number): string[] => {
  return Array.from({ length: count }, () => `${Math.random() * 6 - 3}vw`);
};
const getRandomInt = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const getDynamicStyle = (
  stripsCount: number,
  size: number,
  direction: string
): React.CSSProperties => ({
  display: "grid",
  ...(direction === "horizontal"
    ? {
      gridTemplateRows: `repeat(${stripsCount}, ${size / stripsCount}vw)`,
      width: `${size}vw`,
    }
    : {
      gridTemplateColumns: `repeat(${stripsCount}, ${size / stripsCount}vw)`,
      height: `${size}vw`,
    }),
});

// Components
const Strip: React.FC<StripProps> = ({
  urls,
  index,
  offset,
  shouldAnimate,
  direction
}) => {
  const ref = useRef<HTMLImageElement>(null);
  const transformValue =
    direction === "vertical"
      ? `translateY(${offset[index]})`
      : `translateX(${offset[index]})`;

  const [{ isDragging }, drag] = useDrag(() => ({
    type: "strip",
    item: { urls, index },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  useEffect(() => {
    if (shouldAnimate && ref.current) {
      const transformStart =
        direction === "horizontal" ? "translateX(0)" : "translateY(0)";
      const transformEnd =
        direction === "horizontal"
          ? `translateX(${offset[index]})`
          : `translateY(${offset[index]})`;

      ref.current.style.transform = transformStart;
      void ref.current.offsetWidth;
      ref.current.style.transition = "transform 0.3s ease";
      ref.current.style.transform = transformEnd;
    }
  }, [shouldAnimate, offset, index, direction]);

  drag(ref);

  return (
    <div
      className="s-strips"
      style={{
        opacity: isDragging ? 0.5 : 1,
        transition: "opacity 0.3s ease",
      }}
      data-testid={'strip-item'}
    >
      <img
        ref={ref}
        src={urls}
        alt=""
        className="imgstripes"
        style={{
          transform: transformValue,
          width: "70%",
          boxShadow: "0 1vh 3vh rgba(0, 0, 0, 0.1)",
        }}
      />
    </div>
  );
};

const BorderCell: React.FC<BorderCellProps> = ({
  index,
  strip,
  onDrop,
  onStripClick,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [, drop] = useDrop<StripItem>(() => ({
    accept: "strip",
    drop: (item: StripItem) => onDrop(item, index)
  }));
  const isCorrect = useMemo(() =>
    strip ? strip.index === index : false
    , [strip, index]);
  drop(ref);

  return (
    <div
      ref={ref}
      className={`StripsIn ${isCorrect ? "correct-position" : ""}`}
      data-testid={'border-cell'}
    >
      {strip && (
        <img
          src={strip.urls}
          alt=""
          className="imgstripes"
          onClick={!isCorrect ? () => onStripClick(index) : undefined}
          style={{
            width: "100%",
            height: "100%",
          }}
        />
      )}
    </div>
  );
};

const Board: React.FC<BoardProps> = ({
  setisOpenPanel,
  setisRunningTime,
  isStartGame,
  gameCompleted,
  setGameCompleted,
  pictureSet,
  difficulty,
  onResetGame
}) => {
  // State
  const [direction, setDirection] = useState<"vertical" | "horizontal">("vertical");
  const [strips_count, setStrips_count] = useState<number>(1);
  const [picture_svg, setPicture_svg] = useState<string>("");
  const [strips, setStrips] = useState<StripItem[]>([]);
  const [offsets, setOffsets] = useState<string[]>([]);
   const [isLoading, setIsLoading] = useState<boolean>(true);
  const [borders, setBorders] = useState<(StripItem | null)[]>(
    Array(strips_count).fill(null)
  );
  const [animatedStripIndex, setAnimatedStripIndex] = useState<number | null>(
    null
  );
  const [initialAnimationDone, setInitialAnimationDone] = useState(false);
  const [message, setMessage] = useState<string>(INITIAL_MESSAGE);
  useEffect(() => {
   
    if (!isStartGame && onResetGame) {
    setStrips([]);
    setBorders(Array(strips_count).fill(null));
    setGameCompleted(false);
    setInitialAnimationDone(false);
    setMessage(INITIAL_MESSAGE);
    setisRunningTime(false);
    initializeGame();
    isStartedRef.current = false;

    }
  }, [isStartGame, onResetGame])
  const isStartedRef = useRef(false);
  useEffect(() => {
    if (![1, 2, 3].includes(difficulty ?? defaultDifficulty)) {
      setStrips_count(0);
      return;
    }

    const newCount = difficulty === 1 ? getRandomInt(3, 5)
      : difficulty === 2 ? getRandomInt(7, 7)
        : getRandomInt(9, 12);
    setStrips_count(prev => prev !== newCount ? newCount : prev);
    setBorders(Array(newCount).fill(null));

  }, [difficulty]);
   const initializeGame = useCallback(async () => {
    setIsLoading(true); // Начинаем загрузку
    
    const randomDirection = Math.random() > 0.5 ? "horizontal" : "vertical";
    setDirection(randomDirection);

    try {
      const allSvgPaths = loadSvgImages();
      
      let selectedSvgPaths = allSvgPaths;
      if (pictureSet) {
        const svgNames = pictureSetMap.get(pictureSet);
        if (svgNames && svgNames.length > 0) {
          // Сразу выбираем случайное имя из доступных
          const randomSvgName = svgNames[Math.floor(Math.random() * svgNames.length)];
          // Ищем только нужное изображение
          const foundSvg = allSvgPaths.find((path: string) => path.includes(randomSvgName));
          if (foundSvg) {
            setPicture_svg(foundSvg);
            setIsLoading(false);
            return;
          }
        }
      }
      
      // Если не нашли по имени или pictureSet не указан, берем случайное из всех
      const randomSvg = allSvgPaths[Math.floor(Math.random() * allSvgPaths.length)];
      setPicture_svg(randomSvg);
      
    } catch (error) {
      console.error('Error loading image:', error);
    } finally {
      setIsLoading(false);
    }
  }, [pictureSet]);

  // Инициализация при монтировании
  useEffect(() => {
    initializeGame();
  }, [initializeGame]);


  // Инициализация при монтировании
  useEffect(() => {
    initializeGame();
  }, [initializeGame]);



  // Game logic
  const isGameCompleted = useMemo(() => {
    if (strips.length > 0) return false;

    return borders.every((border, index) =>
      border?.index === index
    );
  }, [borders, strips]);

  const handleDrop = useCallback(
    (item: StripItem, borderIndex: number) => {
      if (gameCompleted) return;

      const newStrips = strips.filter((strip) => strip.index !== item.index);
      const newBorders = [...borders];

      if (newBorders[borderIndex]) {
        setStrips([...newStrips, newBorders[borderIndex]!]);
      } else {
        setStrips(newStrips);
      }

      newBorders[borderIndex] = item;
      setBorders(newBorders);
    },
    [borders, strips, gameCompleted]
  );

  const handleStripClick = useCallback(
    (borderIndex: number) => {
      if (gameCompleted) return;

      const stripToMove = borders[borderIndex];
      if (stripToMove) {
        const newBorders = [...borders];
        newBorders[borderIndex] = null;
        setBorders(newBorders);
        setAnimatedStripIndex(stripToMove.index);
        setStrips([...strips, stripToMove]);
      }
    },
    [borders, strips, gameCompleted]
  );

  const handleStripsReady = useCallback(() => {
    const randstrip = [...strips].sort(() => Math.random() - 0.5);
    setStrips(randstrip);
  }, [strips]);

  const checkCorrectPositions = useCallback(
    (borderIndex: number, strip: StripItem): boolean => {
      return strip.index === borderIndex;
    },
    []
  );

  // Effects
  useEffect(() => {
    if (isGameCompleted) {
      setGameCompleted(true);
      setisRunningTime(false);
      setMessage(WIN_MESSAGE);
    }
  }, [isGameCompleted]);
  useEffect(() => {
    if (isStartGame) {
      setMessage(INITIAL_MESSAGE);
    } else {
      setMessage(START_MESSAGE);
    }
  }, [isStartGame]);

  useEffect(() => {
    if (isStartGame && !isStartedRef.current) {
      isStartedRef.current = true;
      setOffsets(generateOffsets(strips_count));
      handleStripsReady();
      setisRunningTime(true);
      setTimeout(() => setInitialAnimationDone(true), 300);
    }
  }, [isStartGame, handleStripsReady, setisRunningTime, strips_count]);

  useEffect(() => {
    if (animatedStripIndex !== null) {
      const timer = setTimeout(() => setAnimatedStripIndex(null), 300);
      return () => clearTimeout(timer);
    }
  }, [animatedStripIndex]);

  // Render components
  const stripsToRender = useMemo(() => (
    strips.map((strip) => (
      <Strip
        key={strip.index}
        urls={strip.urls}
        index={strip.index}
        offset={offsets}
        shouldAnimate={!initialAnimationDone || strip.index === animatedStripIndex}
        direction={direction}
        
      />
    ))
  ), [strips, offsets, initialAnimationDone, animatedStripIndex, direction]);

  const RenderBorder = useCallback(
    () => (
      <>
        {borders.map((strip, index) => (
          <BorderCell
            key={index}
            index={index}
            strip={strip}
            onDrop={handleDrop}
            onStripClick={handleStripClick}
          />
        ))}
      </>
    ),
    [borders, handleDrop, handleStripClick, checkCorrectPositions]
  );

  const RenderPicture = useCallback(
    () => (
      <div className="startpic">
        <img className="s-picture" src={picture_svg} alt="Ice illustration" />
      </div>
    ),
    [picture_svg]
  );

  const RenderWinImage = useCallback(
    () => (
      <img className="s-picture" id="s-win_img" src="/img/stripes/umnichka.svg" alt="win pict" />
    ),
    []
  );
  if (!picture_svg) return <div>Загрузка...</div>;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="Stripes-Board" data-testid="board-container">
        <CanvasStripper
          imageUrl={picture_svg}
          stripesCount={strips_count}
          direction={direction}
          strips={setStrips}
        />
        <div className="stripes-mainsubtitle">Полоски Лизы</div>

        <div className="stripes-top-bar">
          <div className="stripes-message-bar">
            <span>{message}</span>
          </div>
          <div className="stripes-helpbut">
            <img
              id="s-button"
              title="Помощь"
              src="/img/help.svg"
              alt=""
              onClick={() => setisOpenPanel(true)}
            />
          </div>

        </div>

        <div className="stripes-scene">
          <div
            className="stripes-boxout"
            style={
              isStartGame && !gameCompleted
                ? getDynamicStyle(strips_count, 24, direction)
                : {
                  display: "grid",
                  minHeight: "23vw",
                }
            }
          >
            {isStartGame ? stripsToRender : <RenderPicture />}
            {gameCompleted && <RenderWinImage />}
          </div>

          <div
            className="stripes-boxin"
            style={
              isStartGame && !gameCompleted
                ? getDynamicStyle(strips_count, 34, direction)
                : { display: "grid", width: "34vw" }
            }
          >
            {isStartGame && !gameCompleted && <RenderBorder />}
            {gameCompleted && <RenderPicture />}
          </div>

          {isStartGame && !gameCompleted && <RenderPicture />}
        </div>
      </div>
    </DndProvider>
  );
};

export default Board;


